(() => {
  const THRESHOLD = 7000;
  const COUNTRY_STORAGE = 'moonocase-shipping-country';
  const COST_STORAGE = 'moonocase-shipping-cost';
  const DEFAULT_ADDRESS = {
    US: { province: 'NY', zip: '10001', city: 'New York', countryName: 'United States' },
    CA: { province: 'ON', zip: 'M5V 2T6', city: 'Toronto', countryName: 'Canada' },
    GB: { zip: 'SW1A 1AA', city: 'London', countryName: 'United Kingdom' },
    IL: { zip: '6158000', city: 'Tel Aviv', countryName: 'Israel' },
    AU: { province: 'NSW', zip: '2000', city: 'Sydney', countryName: 'Australia' },
    DE: { zip: '10115', city: 'Berlin', countryName: 'Germany' },
    FR: { zip: '75001', city: 'Paris', countryName: 'France' },
    NL: { zip: '1012 JS', city: 'Amsterdam', countryName: 'Netherlands' },
    IT: { zip: '00118', city: 'Rome', countryName: 'Italy' },
    ES: { zip: '28001', city: 'Madrid', countryName: 'Spain' },
  };

  function formatMoney(cents, format) {
    const value = (Math.abs(Number(cents) || 0) / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const placeholder = format || '${{amount}}';
    return placeholder
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
  }

  function decodeEntities(value) {
    if (!value || !value.includes('&')) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function remainingCopy(remainingMoney) {
    const template = decodeEntities(
      window.cartStrings?.freeShippingRemaining || "You're [remaining] away from free shipping"
    );
    return template.replace('[remaining]', remainingMoney);
  }

  function remainingHtml(remainingMoney) {
    const htmlTemplate = window.cartStrings?.freeShippingRemainingHtml;
    if (htmlTemplate) {
      return decodeEntities(htmlTemplate).replace('[remaining]', remainingMoney);
    }
    return remainingCopy(remainingMoney)
      .replace(remainingMoney, `<span class="shipping-progress__amount">${remainingMoney}</span>`)
      .replace(/free shipping/i, '<span class="shipping-progress__free">free shipping</span>');
  }

  function unlockedHtml() {
    const htmlTemplate = window.cartStrings?.freeShippingUnlockedHtml;
    if (htmlTemplate) return decodeEntities(htmlTemplate);
    return decodeEntities(window.cartStrings?.freeShippingUnlocked || "You've unlocked free shipping").replace(
      /free shipping/i,
      '<span class="shipping-progress__free">free shipping</span>'
    );
  }

  function rateAmount(rate) {
    if (!rate || typeof rate !== 'object') return NaN;
    const raw = rate.price ?? rate.presentment_price ?? rate.total_price ?? rate.estimatedCost?.amount;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') return parseFloat(raw.replace(',', '.'));
    return NaN;
  }

  function cheapestFromRates(rates) {
    const priced = (Array.isArray(rates) ? rates : [])
      .map((rate) => ({ ...rate, price: rateAmount(rate) }))
      .filter((rate) => Number.isFinite(rate.price));
    if (!priced.length) return null;
    return priced.reduce((min, rate) => (rate.price < min.price ? rate : min), priced[0]);
  }

  function addressFromIso(iso, el) {
    const code = (iso || '').toUpperCase();
    const fallback = DEFAULT_ADDRESS[code] || DEFAULT_ADDRESS.US;
    const isVisitor = el && code === (el.dataset.country || window.Shopify?.country || '').toUpperCase();
    return {
      iso: code || 'US',
      countryName: (isVisitor && el.dataset.countryName) || fallback.countryName || code || 'United States',
      province: (isVisitor && el.dataset.province) || fallback.province || '',
      zip: (isVisitor && el.dataset.zip) || fallback.zip || '10001',
      city: fallback.city || 'City',
    };
  }

  function addressFromEl(el) {
    return addressFromIso(el.dataset.country || window.Shopify?.country || 'US', el);
  }

  function addressQuery(address, useName) {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', useName ? address.countryName || address.iso : address.iso);
    if (address.province) params.set('shipping_address[province]', address.province);
    if (address.zip) params.set('shipping_address[zip]', address.zip);
    if (address.city) params.set('shipping_address[city]', address.city);
    return params.toString();
  }

  async function fetchJson(url, options = {}, timeoutMs = 5000) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        credentials: options.credentials ?? 'include',
        ...options,
        signal: ctrl.signal,
        headers: {
          Accept: 'application/json, text/javascript, */*',
          ...(options.headers || {}),
        },
      });
      if (!res.ok) return null;
      const text = await res.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (err) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function firstRates(jobs, timeoutMs) {
    if (!jobs.length) return Promise.resolve([]);

    return new Promise((resolve) => {
      let pending = jobs.length;
      let settled = false;
      const timer = setTimeout(() => finish([]), timeoutMs);

      const finish = (rates) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(rates);
      };

      jobs.forEach((job) => {
        Promise.resolve(job)
          .then((rates) => {
            if (Array.isArray(rates) && rates.length) {
              finish(rates);
              return;
            }
            pending -= 1;
            if (pending <= 0) finish([]);
          })
          .catch(() => {
            pending -= 1;
            if (pending <= 0) finish([]);
          });
      });
    });
  }

  function ratesFromPayload(data) {
    const list = data?.shipping_rates || data?.shippingRates || [];
    return Array.isArray(list) ? list : [];
  }

  async function ajaxRates(root, address) {
    const queries = [addressQuery(address, false), addressQuery(address, true)];
    for (const qs of queries) {
      const sync = await fetchJson(`${root}cart/shipping_rates.json?${qs}`, {}, 4000);
      const syncRates = ratesFromPayload(sync);
      if (syncRates.length) return syncRates;

      await fetchJson(
        `${root}cart/prepare_shipping_rates.json?${qs}`,
        {
          method: 'POST',
          headers: { Accept: 'application/json' },
        },
        4000
      );

      for (let i = 0; i < 5; i += 1) {
        const asyncData = await fetchJson(`${root}cart/async_shipping_rates.json?${qs}`, {}, 4000);
        const asyncRates = ratesFromPayload(asyncData);
        if (asyncRates.length) return asyncRates;
        if (asyncData && Array.isArray(asyncData.shipping_rates)) break;
        await delay(300);
      }
    }
    return [];
  }

  function isProbeItem(item) {
    const props = item?.properties || {};
    return props._shipping_probe === '1';
  }

  async function stripProbeItems() {
    const cart = await loadCart().catch(() => null);
    const probes = (cart?.items || []).filter(isProbeItem);
    if (!probes.length) return { cart, stripped: false };

    const root = cartRoot();
    for (const item of probes) {
      await fetchJson(
        `${root}cart/change.js`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        },
        5000
      );
    }
    cartRequest = null;
    const next = await loadCart().catch(() => cart);
    const leftover = (next?.items || []).filter(isProbeItem);
    return { cart: next, stripped: leftover.length < probes.length };
  }

  function storefrontToken() {
    try {
      const el = document.getElementById('shopify-features');
      if (!el) return '';
      const data = JSON.parse(el.textContent);
      return data.accessToken || '';
    } catch (err) {
      return '';
    }
  }

  async function storefrontRates(variantId, address, shop) {
    const token = storefrontToken();
    if (!token || !variantId || !address.iso) return [];

    const versions = ['2024-10'];
    const endpoints = [`/api/${versions[0]}/graphql.json`];
    if (shop) endpoints.push(`https://${shop}/api/${versions[0]}/graphql.json`);

    const createQuery = `
      mutation Estimate($cartInput: CartInput!, $country: CountryCode!) {
        cartCreate(input: $cartInput) @inContext(country: $country) {
          cart { id }
          userErrors { message }
        }
      }
    `;

    const pollQuery = (withCarrierRates) => `
      query CartDelivery($id: ID!, $country: CountryCode!) {
        cart(id: $id) @inContext(country: $country) {
          deliveryGroups(first: 10${withCarrierRates ? ', withCarrierRates: true' : ''}) {
            nodes {
              deliveryOptions {
                title
                estimatedCost { amount currencyCode }
              }
            }
            edges {
              node {
                deliveryOptions {
                  title
                  estimatedCost { amount currencyCode }
                }
              }
            }
          }
        }
      }
    `;

    const country = String(address.iso || '').toUpperCase();
    const deliveryAddress = {
      address1: '1 Main Street',
      city: address.city || 'City',
      zip: address.zip || '00000',
      countryCode: country,
    };
    if (address.province) deliveryAddress.provinceCode = address.province;

    const variantGid = String(variantId).includes('gid://')
      ? String(variantId)
      : `gid://shopify/ProductVariant/${variantId}`;

    const variables = {
      country,
      cartInput: {
        lines: [
          {
            quantity: 1,
            merchandiseId: variantGid,
          },
        ],
        buyerIdentity: {
          countryCode: country,
          deliveryAddressPreferences: [{ deliveryAddress }],
        },
      },
    };

    const optionsFromGroups = (groups) =>
      (groups || []).flatMap((group) => group.deliveryOptions || []);

    const groupsFromCart = (cart) => {
      const conn = cart?.deliveryGroups;
      if (!conn) return [];
      if (Array.isArray(conn.nodes) && conn.nodes.length) return conn.nodes;
      if (Array.isArray(conn.edges)) return conn.edges.map((edge) => edge.node).filter(Boolean);
      return [];
    };

    const ratesFromOptions = (options) =>
      options
        .map((option) => ({ price: option?.estimatedCost?.amount }))
        .filter((rate) => rate.price != null);

    const pollRates = async (endpoint, cartId, withCarrierRates) => {
      const pollJson = await fetchJson(endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: pollQuery(withCarrierRates),
          variables: { id: cartId, country },
        }),
      }, 5000);
      if (!pollJson?.data) return [];
      return ratesFromOptions(optionsFromGroups(groupsFromCart(pollJson.data.cart)));
    };

    for (const endpoint of endpoints) {
      try {
        const res = await fetchJson(endpoint, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query: createQuery, variables }),
        }, 5000);
        if (!res) continue;
        const cartId = res?.data?.cartCreate?.cart?.id;
        if (!cartId) continue;

        for (let i = 0; i < 6; i += 1) {
          if (i > 0) await delay(300);
          let rates = await pollRates(endpoint, cartId, true);
          if (rates.length) return rates;
          rates = await pollRates(endpoint, cartId, false);
          if (rates.length) return rates;
        }
      } catch (err) {
        // try next endpoint
      }
    }
    return [];
  }

  async function ratesForAddress(root, address, variantId, shop, itemCount) {
    if (itemCount >= 1) {
      const live = await ajaxRates(root, address);
      if (live.length) return live;
    }
    if (variantId) {
      const remote = await storefrontRates(variantId, address, shop);
      if (remote.length) return remote;
    }
    return [];
  }

  async function catalogVariantId() {
    const data = await fetchJson(`${cartRoot()}products.json?limit=1`, {}, 4000);
    return String(data?.products?.[0]?.variants?.[0]?.id || '');
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  let cartRequest = null;
  function loadCart() {
    if (!cartRequest) {
      cartRequest = fetchJson(`${cartRoot()}cart.js`, {}, 5000).finally(() => {
        cartRequest = null;
      });
    }
    return cartRequest;
  }

  function isFullCart(cart) {
    return Boolean(cart && typeof cart === 'object' && Array.isArray(cart.items) && 'item_count' in cart);
  }

  function merchandiseCents(cart) {
    if (!isFullCart(cart)) return null;

    const items = cart.items.filter((item) => !isProbeItem(item));
    const lineSum = items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      if (qty <= 0) return sum;
      const line = Number(item.final_line_price);
      if (Number.isFinite(line)) return sum + line;
      const linePrice = Number(item.line_price);
      if (Number.isFinite(linePrice)) return sum + linePrice;
      const unit = Number(item.discounted_price ?? item.price);
      return sum + (Number.isFinite(unit) ? unit * qty : 0);
    }, 0);

    return lineSum;
  }

  const uiMemory = {
    seq: 0,
    progress: null,
    unlocked: null,
    statusHtml: null,
    costText: null,
    costCountry: null,
    rateMode: null,
  };

  function prefersReducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  }

  function persistCost(country, text) {
    if (!text) return;
    uiMemory.costText = text;
    uiMemory.costCountry = country || '';
    try {
      sessionStorage.setItem(COST_STORAGE, JSON.stringify({ country: country || '', text }));
    } catch (err) {
      // ignore
    }
  }

  function knownCost(country) {
    const freeLabel = window.cartStrings?.shippingCostFree || 'Free';
    const usable = (text) => text && text !== freeLabel && text !== '—';
    if (usable(uiMemory.costText) && (!uiMemory.costCountry || !country || uiMemory.costCountry === country)) {
      return uiMemory.costText;
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem(COST_STORAGE) || 'null');
      if (usable(saved?.text) && (!country || !saved.country || saved.country === country)) return saved.text;
    } catch (err) {
      // ignore
    }
    return null;
  }

  function paintCost(el, rates, format) {
    const cheapest = cheapestFromRates(rates);
    if (!cheapest) return false;
    const amount = parseFloat(cheapest.price);
    if (!Number.isFinite(amount)) return false;
    if (amount <= 0) {
      el.textContent = window.cartStrings?.shippingCostFree || 'Free';
      uiMemory.costText = el.textContent;
      return true;
    }
    el.textContent = formatMoney(Math.round(amount * 100), format);
    uiMemory.costText = el.textContent;
    return true;
  }

  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.restoreSavedCountry();
      this.bindCountrySelect();
      const known = knownCost(this.dataset.country);
      if (known) {
        const cost = this.querySelector('[data-shipping-cost]');
        if (cost) cost.textContent = known;
        this.setRateMode('location');
        this.setCostLoading(false);
      }
      this.syncFromDataset();
      this.refreshMerchandise();

      if (!this._unsub && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'shipping-progress') return;
          this.refreshMerchandise();
        });
      }
    }

    disconnectedCallback() {
      this._syncGen = (this._syncGen || 0) + 1;
      if (this._appliedSeq === uiMemory.seq) {
        uiMemory.progress = this.progressRatio();
        uiMemory.unlocked = this.classList.contains('is-unlocked');
      }
      if (this._unsub) {
        this._unsub();
        this._unsub = null;
      }
    }

    restoreSavedCountry() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(COUNTRY_STORAGE) || 'null');
        if (!saved?.iso) return;
        const current = (this.dataset.country || '').toUpperCase();
        if (saved.iso.toUpperCase() === current) return;
        this.dataset.country = saved.iso;
        this.dataset.countryName = saved.name || '';
        this.dataset.userCountry = 'true';
        const select = this.querySelector('[data-shipping-country]');
        if (select && [...select.options].some((option) => option.value === saved.iso)) {
          select.value = saved.iso;
        }
      } catch (err) {
        // ignore storage errors
      }
    }

    bindCountrySelect() {
      const select = this.querySelector('[data-shipping-country]');
      if (!select || select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const iso = select.value;
        const name = option?.getAttribute('data-name') || option?.textContent?.trim() || iso;
        this.dataset.country = iso;
        this.dataset.countryName = name;
        this.dataset.province = '';
        this.dataset.zip = '';
        this.dataset.userCountry = 'true';
        try {
          sessionStorage.setItem(COUNTRY_STORAGE, JSON.stringify({ iso, name }));
        } catch (err) {
          // ignore
        }
        this.setCostLoading(true);
        this.refreshMerchandise();
      });
    }

    setRateMode(mode) {
      uiMemory.rateMode = mode;
      this.classList.toggle('is-estimate', mode === 'estimate');
      this.classList.toggle('needs-country', mode === 'choose');
      const label = this.querySelector('[data-rate-label]');
      if (!label) return;
      const strings = window.cartStrings || {};
      if (mode === 'location') {
        label.textContent = strings.shippingForLocation || 'Shipping calculated for your location:';
      } else if (mode === 'estimate') {
        label.textContent = strings.shippingEstimated || 'Estimated shipping:';
      } else {
        label.textContent = strings.shippingEstimatePrompt || 'Shipping calculated at checkout';
      }
    }

    syncCountrySelect(iso) {
      if (!iso) return;
      const select = this.querySelector('[data-shipping-country]');
      if (!select) return;
      if ([...select.options].some((option) => option.value === iso)) {
        select.value = iso;
      }
    }

    progressRatio() {
      const threshold = Number(this.dataset.threshold || THRESHOLD);
      if (!threshold) return 0;
      return Math.max(0, Math.min(1, this.displayTotal() / threshold));
    }

    setBarProgress(ratio, animate) {
      const bar = this.querySelector('.shipping-progress__bar');
      const clamped = Math.max(0, Math.min(1, Number(ratio) || 0));
      const from = uiMemory.progress;
      const widthFor = (value) => `${(value * 100).toFixed(2)}%`;
      uiMemory.seq += 1;
      this._appliedSeq = uiMemory.seq;
      uiMemory.progress = clamped;

      if (!bar) return;

      bar.style.removeProperty('transform');

      const canAnimate =
        animate && !prefersReducedMotion() && from != null && Math.abs(from - clamped) > 0.002;

      if (canAnimate) {
        bar.style.transition = 'none';
        bar.style.width = widthFor(from);
        void bar.offsetWidth;
        bar.style.removeProperty('transition');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            bar.style.width = widthFor(clamped);
          });
        });
        return;
      }

      bar.style.width = widthFor(clamped);
    }

    applyCart(cart) {
      const total = merchandiseCents(cart);
      if (total == null) return false;
      const visibleCount = cart.items
        .filter((item) => !isProbeItem(item))
        .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      this.dataset.cartTotal = String(total);
      this.dataset.itemCount = String(visibleCount);
      this.syncFromDataset();
      return true;
    }

    refreshMerchandise() {
      const gen = (this._syncGen = (this._syncGen || 0) + 1);
      stripProbeItems()
        .then(({ cart, stripped }) => {
          if (gen !== this._syncGen) return;
          this.applyCart(cart);
          if (stripped && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shipping-progress' });
          }
          this.fetchShippingCost(gen);
        })
        .catch(() => {
          if (gen !== this._syncGen) return;
          this.fetchShippingCost(gen);
        });
    }

    displayTotal() {
      return Number(this.dataset.cartTotal || 0);
    }

    syncFromDataset() {
      const threshold = Number(this.dataset.threshold || THRESHOLD);
      const total = this.displayTotal();
      const remaining = Math.max(0, threshold - total);
      const pct = Math.max(0, Math.min(100, (total / threshold) * 100));
      const unlocked = total >= threshold;
      const format = this.dataset.moneyFormat;
      const freeLabel = window.cartStrings?.shippingCostFree || 'Free';
      const wasUnlocked = uiMemory.unlocked;

      if (
        wasUnlocked != null &&
        wasUnlocked !== unlocked &&
        !prefersReducedMotion()
      ) {
        this.classList.toggle('is-unlocked', wasUnlocked);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.classList.toggle('is-unlocked', unlocked);
          });
        });
      } else {
        this.classList.toggle('is-unlocked', unlocked);
      }
      uiMemory.unlocked = unlocked;

      const track = this.querySelector('.shipping-progress__track');
      const cost = this.querySelector('[data-shipping-cost]');

      this.setBarProgress(pct / 100, true);
      if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));

      this.setStatusCopy(unlocked, remaining, format);

      if (cost) {
        if (unlocked) {
          this.setCostLoading(false);
          this.setRateMode('location');
          cost.textContent = freeLabel;
          uiMemory.costText = freeLabel;
        } else if (cost.textContent.trim() === freeLabel) {
          this.setCostLoading(true);
        }
      }
    }

    setStatusCopy(unlocked, remaining, format) {
      const statusText =
        this.querySelector('[data-shipping-status-text]') || this.querySelector('[data-shipping-status]');
      if (!statusText) return;

      const next = unlocked ? unlockedHtml() : remainingHtml(formatMoney(remaining, format));
      if (statusText.innerHTML === next) {
        uiMemory.statusHtml = next;
        this._statusReady = true;
        return;
      }

      const gen = (this._statusGen = (this._statusGen || 0) + 1);
      const reduce = prefersReducedMotion();
      const remountedAcrossChange =
        !this._statusReady && uiMemory.statusHtml && uiMemory.statusHtml !== next;

      if (remountedAcrossChange) {
        statusText.innerHTML = uiMemory.statusHtml;
        this._statusReady = true;
        void statusText.offsetWidth;
      }

      if (!this._statusReady || reduce) {
        statusText.innerHTML = next;
        uiMemory.statusHtml = next;
        this._statusReady = true;
        return;
      }

      const apply = () => {
        if (gen !== this._statusGen) return;
        statusText.innerHTML = next;
        uiMemory.statusHtml = next;
        statusText.classList.remove('is-leave');
        statusText.classList.add('is-enter');
        statusText.addEventListener(
          'animationend',
          () => {
            if (gen === this._statusGen) statusText.classList.remove('is-enter');
          },
          { once: true }
        );
      };

      statusText.classList.remove('is-enter');
      statusText.classList.add('is-leave');

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        statusText.removeEventListener('transitionend', onEnd);
        apply();
      };
      const onEnd = (event) => {
        if (event.target !== statusText || event.propertyName !== 'opacity') return;
        finish();
      };

      statusText.addEventListener('transitionend', onEnd);
      setTimeout(finish, 280);
    }

    setCostLoading(loading) {
      this.classList.toggle('is-loading-cost', Boolean(loading));
      this.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    async fetchShippingCost(gen) {
      const requestGen = gen || this._syncGen || 0;
      const costEl = this.querySelector('[data-shipping-cost]');
      if (!costEl) return;

      const stillCurrent = () => requestGen === (this._syncGen || 0);
      const threshold = Number(this.dataset.threshold || THRESHOLD);
      if (this.displayTotal() >= threshold) {
        const freeLabel = window.cartStrings?.shippingCostFree || 'Free';
        this.setCostLoading(false);
        this.setRateMode('location');
        costEl.textContent = freeLabel;
        uiMemory.costText = freeLabel;
        return;
      }

      const itemCount = Number(this.dataset.itemCount || 0);
      let variantId = this.dataset.variantId;
      const format = this.dataset.moneyFormat;
      const root = cartRoot();
      const visitor = addressFromEl(this);
      const userPicked = this.dataset.userCountry === 'true';
      const known = knownCost(visitor.iso);

      if (known) {
        costEl.textContent = known;
        this.setRateMode('location');
        this.setCostLoading(false);
      } else {
        this.setCostLoading(true);
      }

      try {
        if (itemCount >= 1) {
          const cart = await loadCart().catch(() => null);
          if (!stillCurrent()) return;
          variantId = String(cart?.items?.[0]?.variant_id || cart?.items?.[0]?.id || variantId || '');
        }
        if (!variantId) {
          variantId = await catalogVariantId();
          if (!stillCurrent()) return;
        }

        let rates = await ratesForAddress(root, visitor, variantId, this.dataset.shop, itemCount);

        if (!rates.length && !userPicked) {
          const fallback = addressFromIso(this.dataset.fallbackCountry || 'US', this);
          if (fallback.iso !== visitor.iso) {
            rates = await ratesForAddress(root, fallback, variantId, this.dataset.shop, itemCount);
          }
        }

        if (!stillCurrent()) return;
        if (paintCost(costEl, rates, format)) {
          this.setRateMode('location');
          const freeLabel = window.cartStrings?.shippingCostFree || 'Free';
          if (costEl.textContent !== freeLabel) persistCost(visitor.iso, costEl.textContent);
          return;
        }

        if (known) {
          costEl.textContent = known;
          this.setRateMode('location');
          return;
        }

        this.setRateMode('choose');
        costEl.textContent = '';
      } finally {
        if (stillCurrent()) this.setCostLoading(false);
      }
    }
  }

  if (!customElements.get('shipping-progress')) {
    customElements.define('shipping-progress', ShippingProgress);
  }
})();
